import React, { useState, useRef } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { Facebook, Instagram, Linkedin, ChevronDown } from "lucide-react";
import emailjs from "@emailjs/browser";

import {
  ContactSection,
  ContactInfo,
  FormContainer,
  Form,
  NameFields,
  FormGroup,
  Label,
  Input,
  TextArea,
  SubmitButton,
  ContactItem,
  ContactRow,
  ContactColumn,
  OfficeRow,
  OfficeColumn,
  SocialLinks,
  SocialLink,
} from "./ContactStyles";

const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

export default function ContactUs() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    countryCode: "+44",
    phone: "",
    email: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const contactRef = useRef(null);
  const contactControls = useAnimation();
  const contactInView = useInView(contactRef, { once: true });

  React.useEffect(() => {
    if (contactInView) {
      contactControls.start("visible");
    }
  }, [contactInView, contactControls]);

  const countryCodes = [
    { code: "+44", label: "UK (+44)" },
    { code: "+231", label: "Liberia (+231)" },
    { code: "+234", label: "Nigeria (+234)" },
    { code: "+233", label: "Ghana (+233)" },
    { code: "+254", label: "Kenya (+254)" },
    { code: "+27", label: "South Africa (+27)" },
    { code: "+20", label: "Egypt (+20)" },
    { code: "+251", label: "Ethiopia (+251)" },
    { code: "+212", label: "Morocco (+212)" },
    { code: "+49", label: "Germany (+49)" },
    { code: "+33", label: "France (+33)" },
    { code: "+31", label: "Netherlands (+31)" },
    { code: "+34", label: "Spain (+34)" },
    { code: "+353", label: "Ireland (+353)" },
    { code: "+1", label: "US/Canada (+1)" },
    { code: "+971", label: "UAE (+971)" },
    { code: "+91", label: "India (+91)" },
    { code: "+61", label: "Australia (+61)" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || "service_fykav3d";
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || "template_vwgdlhg";
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || "your_public_key_here";

    const templateParams = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phone: `${formData.countryCode}${formData.phone.trim()}`,
      email: formData.email.trim(),
      message: formData.message.trim(),
    };

    try {
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      setSubmitStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        countryCode: "+44",
        phone: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("EmailJS send error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection ref={contactRef}>
      {/* Left side - Contact Info */}
      <motion.div initial="hidden" animate={contactControls} variants={fadeInLeft}>
        <ContactInfo>
          <h2>Get in touch</h2>

          <ContactItem>
            <h3>Address</h3>
            <OfficeRow>
              <OfficeColumn>
                <strong>UK Office</strong>
                <p>Kemp House, 128 City Road,</p>
                <p>London, England, EC1V 2NX</p>
                <p>+44 (0) 7753 936903</p>
              </OfficeColumn>
              <OfficeColumn>
                <strong>Liberia Office</strong>
                <p>Horton House, Horton Avenue,</p>
                <p>Monrovia, Liberia</p>
                <p>+231 (0) 7774 47440</p>
              </OfficeColumn>
            </OfficeRow>
          </ContactItem>

          <ContactItem>
            <ContactRow>
              <ContactColumn>
                <h3>Email</h3>
                <p>enquiries@augusttwenty.co.uk</p>
              </ContactColumn>
            </ContactRow>
          </ContactItem>

          <ContactItem>
            <h3>Social Media</h3>
            <SocialLinks>
              <SocialLink href="https://www.facebook.com/athortonhouse" target="_blank" rel="noopener noreferrer">
                <Facebook size={24} />
              </SocialLink>
              <SocialLink href="https://www.instagram.com/athortonhouse/" target="_blank" rel="noopener noreferrer">
                <Instagram size={24} />
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/company/august-twenty-limited/" target="_blank" rel="noopener noreferrer">
                <Linkedin size={24} />
              </SocialLink>
            </SocialLinks>
          </ContactItem>
        </ContactInfo>
      </motion.div>

      {/* Right side - Form */}
      <motion.div initial="hidden" animate={contactControls} variants={fadeInRight}>
        <FormContainer>
          <Form onSubmit={handleSubmit}>
            <NameFields>
              <FormGroup>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormGroup>
            </NameFields>

            {/* Updated Phone Field */}
            <FormGroup>
              <Label htmlFor="phone">Phone Number</Label>
              <div style={{
                display: 'flex',
                width: '100%',
                position: 'relative',
                background: '#1a1a1f',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #333',
              }}>
                <div style={{
                  position: 'relative',
                  width: '160px',
                  minWidth: '160px',
                  flexShrink: 0,
                }}>
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      height: '48px',
                      padding: '0 36px 0 14px',
                      background: '#2a2a32',
                      color: '#e0e0e0',
                      border: 'none',
                      fontSize: '16px',
                      appearance: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    color="#aaa"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                    }}
                  />
                </div>

                <div style={{
                  position: 'absolute',
                  left: '160px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '1px',
                  height: '24px',
                  backgroundColor: '#444',
                  pointerEvents: 'none',
                  zIndex: 1,
                }} />

                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderRadius: '0 8px 8px 0',
                    background: '#2a2a32',
                    color: '#e0e0e0',
                    padding: '14px',
                  }}
                />
              </div>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message *</Label>
              <TextArea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Message"}
            </SubmitButton>

            {submitStatus === "success" && (
              <p style={{ color: "#22c55e", marginTop: "1rem", textAlign: "center" }}>
                Thank you! Your message has been sent successfully. We'll get back to you soon.
              </p>
            )}
            {submitStatus === "error" && (
              <p style={{ color: "#ef4444", marginTop: "1rem", textAlign: "center" }}>
                Sorry, something went wrong. Please try again later or email us directly at{" "}
                <a href="mailto:enquiries@augusttwenty.co.uk">enquiries@augusttwenty.co.uk</a>.
              </p>
            )}
          </Form>
        </FormContainer>
      </motion.div>
    </ContactSection>
  );
}